import React from "react";
import i18n from "i18next";
import styled, { css } from "styled-components";
import { connect, ReactReduxContext } from "react-redux";
import {
  uploadAiImportDocument,
  currentPortfolioSelector,
  getUuid,
  insertCustodianAtEndOfSection,
  insertSection,
  updateUserPreferences,
  userPreferencesSelector,
  aiSupportedFileTypesSelector,
  rescanAiImportDocument,
  showBlackPaywallIfQualifies,
  showToastTip,
  fetchTickerDetails,
  convertCustodianHistoryApiDateFormatToUIFormat,
  converUIDateFormatToCustodianHistoryApiDateFormat,
  guessDateInCustodianHistoryUIFormat,
  getCustodianCashflow,
  getSortKeyBetween,
  tickerTypes,
  tickerSubTypes
} from "@kubera/common";
import { withRouter } from "@kubera/common";
import SecondaryButton from "components/button/SecondaryButton";
import PrimaryButton from "components/button/PrimaryButton";
import GridComponentWrapper from "components/grid/GridComponentWrapper";
import {
  GridData,
  GridSheetData,
  GridSectionData,
  GridRowData,
  GridColumnData,
  GridCellData,
  CurrencyCellData,
  cellType
} from "components/grid/GridDataModel";
import ContextMenu, { contextMenuItemType } from "components/contextmenu/ContextMenu";
import CustodiansPickerDialog from "components/planning/variable_dialogs/CustodiansPickerDialog";
import { category } from "components/dashboard/DashboardComponentExports";
import {
  itemState,
  getBadgeColorForItemState,
  itemStateBadgeCellWidth,
  itemStateBadgeMarginRight,
  buildCashflowLocationMap,
  setMatchedCashflowIds,
  getCashflowItemDetails,
  syncCashflowsToPortfolio,
  createBulkSyncCollector,
  flushBulkSync,
  bulkEntityType,
  buildCashflowLastLink,
  getLocationPath
} from "./AiImportExports";
import ConfirmationDialog from "components/dialog/ConfirmationDialog";
import OptionsIcon from "assets/images/options.svg?react";
import expandableIndicatorIcon from "assets/images/expandable_indicator.svg";
import swapIcon from "assets/images/swap.svg?react";
import Loader from "components/loader/Loader";

const cashFlowType = {
  CASH_IN: "cash-in",
  CASH_OUT: "cash-out"
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  margin-top: 30px;
`;

const ImportButton = styled(PrimaryButton)`
  min-width: 125px;
  padding-left: 20px;
  padding-right: 20px;
  background-color: ${props => (props.isDisabled && !props.isLoading === true ? "#D2D2D2" : "#029600")};
  color: white;
`;

const RescanHelpText = styled.div`
  display: flex;
  align-items: center;
  width: 60px;
  margin-left: 20px;
  font-weight: 400;
  font-size: 10px;
  line-height: 130%;
  letter-spacing: 0%;
  text-align: right;
  text-transform: uppercase;
  color: #878787;
  white-space: pre-wrap;
`;

const RescanButton = styled(SecondaryButton)`
  margin-left: 6px;
  min-width: 50px;
  padding-left: 20px;
  padding-right: 20px;
`;

const ResultsContainer = styled.div`
  margin-top: 22px;
`;

const GridContainer = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-top: 0;
  margin: ${props => props.margin};
`;

const PartialResultMessage = styled.div`
  width: 910px;
  white-space: pre-line;
  color: #333333;
  padding: 10px 20px 10px 20px;
  font-weight: 400;
  font-size: 12px;
  line-height: 125%;
  letter-spacing: 0%;
  background: #ffe600;
`;

const Grid = styled(GridComponentWrapper)`
  flex: 1;
  margin-left: -1px;
  margin-right: -1px;
  margin-top: -1px;
`;

const BadgeContainer = styled.div`
  display: flex;
  margin-left: -4px;
  margin-right: ${itemStateBadgeMarginRight};
  min-width: 30px;
`;

const BadgeText = styled.div`
  display: flex;
  height: 15px;
  background: ${props => props.bgColor};
  color: ${props => (props.itemState === itemState.NEW || props.isDisabled ? "#FFFFFF" : "#00000099")};
  border: ${props =>
    props.itemState === itemState.IGNORED || props.itemState === itemState.UNKNOWN ? "1px solid #BCBCBC" : "none"};
  width: 100%;
  align-items: center;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 15px;
  text-align: right;
  font-weight: 400;
  font-size: 9px;
  line-height: 9px;
  letter-spacing: 0%;
  text-transform: uppercase;
`;

const SectionTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-top: ${props => (props.isFirst ? "" : "1px solid rgba(0, 0, 0, 0.1)")};
`;

const SectionPadding = styled.div`
  min-height: 20px;
  border-left: 1px solid rgba(252, 252, 252);
  border-right: 1px solid rgba(252, 252, 252);
`;

const SectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 10px 12px 10px;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
`;

const ExpandIcon = styled.div`
  width: 9px;
  height: 19px;
  margin-right: 16px;
  background-color: transparent;
  background-image: url("${expandableIndicatorIcon}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 9px 9px;
  transform: ${props => (props.isCollapsed ? "rotate(270deg)" : "rotate(0deg)")};
`;

const SectionDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
`;

const SectionName = styled.div`
  max-width: 250px;
  font-weight: 400;
  font-size: 14px;
  white-space: break-spaces;
`;

const Arrow = styled.div`
  margin-left: 14px;
  margin-right: 14px;
  color: #000;
  font-feature-settings: "ss01" on, "calt" off;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const CustodianName = styled(SectionName)`
  max-width: none;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props => (props.isCustodianPath ? "#000000" : "#0074FC")};
`;

const SectionDescription = styled.div`
  margin-top: 3px;
  font-weight: 400;
  font-size: 10px;
  line-height: 125%;
  white-space: pre-line;
  letter-spacing: 0%;
  color: #6f6f6f;
`;

const SectionState = styled.div`
  flex: 1;
  text-align: right;
  margin-right: 10px;
  font-weight: 400;
  font-size: 13px;
  color: ${props => (props.isImported ? "#00a707" : "#00000080")};
`;

const SectionOptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 40px;
  outline: 0;
  padding: 0;
  border: 0;
  margin: 0;
  margin-right: -10px;
  margin-left: auto;
  cursor: pointer;
  background-color: transparent;

  &:hover {
    background-color: ${props => props.theme.focusBackgroundColor};
  }

  &:focus {
    outline: 2px solid ${props => props.theme.gridCellSelectedOutlineColor};
    outline-offset: -1px;
  }
`;

const OptionsIconComponent = styled(OptionsIcon)`
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const CashflowsLoader = styled(Loader)`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.popoverBackgroundColor};
  z-index: 1000;
`;

const SwapCashflowIcon = styled(swapIcon)`
  height: 100%;
  padding-left: 15px;
  padding-right: 15px;
  opacity: 0.5;
  cursor: pointer;
`;

class AiCashflowImportComponent extends React.Component {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);

    this.state = {
      dataExtracted: null,
      custodianNameCashflowMap: null,
      gridData: null,
      showLocationPickerForSection: null,
      showContextMenuForSection: null,
      disableImportButton: false,
      isLoadingCashflow: false,
      isImportingCustodianNames: null,
      showLocationPickerForRow: null,
      showLocationPickerForRowSection: null,
      showCombinedSyncConfirmation: false
    };

    this.locationMap = {};
    this.defaultLocationMap = {};
    this.accountLocationMap = {};
    this.newCustodianIdByAccountId = {};
    this.userOverriddenSectionNames = new Set();
    this.ignoredItemIds = [];
    this.ignoredCustodianNames = [];
    this.importedItemIds = [];
    this.importedCustodianNames = [];
    this.contextMenuRef = React.createRef();

    this.handleGridChange = this.handleGridChange.bind(this);
    this.handleRowContextMenuSelection = this.handleRowContextMenuSelection.bind(this);
    this.handleSectionLocationSelection = this.handleSectionLocationSelection.bind(this);
    this.handleSectionLocationPickerDismiss = this.handleSectionLocationPickerDismiss.bind(this);
    this.handleImportButtonClick = this.handleImportButtonClick.bind(this);
    this.handleSectionNameClick = this.handleSectionNameClick.bind(this);
    this.handleSectionOptionButtonClick = this.handleSectionOptionButtonClick.bind(this);
    this.handleSectionContextMenuSelection = this.handleSectionContextMenuSelection.bind(this);
    this.handleCellBlur = this.handleCellBlur.bind(this);
    this.handleRowLocationSelection = this.handleRowLocationSelection.bind(this);
    this.handleRowLocationPickerDismiss = this.handleRowLocationPickerDismiss.bind(this);
    this.handleAccountLocationsUpdate = this.handleAccountLocationsUpdate.bind(this);
    this.runSync = this.runSync.bind(this);
  }

  componentDidMount() {
    this.handleExtractedDataResponse(this.props.dataExtracted);
    this.props.registerSyncFn?.(this.props.tabKey, this.runSync);
    this.props.registerAccountLocationsListener?.(this.props.tabKey, this.handleAccountLocationsUpdate);
  }

  componentWillUnmount() {
    this.props.registerSyncFn?.(this.props.tabKey, null);
    this.props.registerAccountLocationsListener?.(this.props.tabKey, null);
  }

  // A created account resolves to its real custodian here, so it is no longer treated as new.
  // User-repointed and already-synced sections are left untouched.
  handleAccountLocationsUpdate(accountLocationMap, newCustodianIdByAccountId) {
    this.accountLocationMap = accountLocationMap || {};
    this.newCustodianIdByAccountId = newCustodianIdByAccountId || {};

    if (!this.state.gridData) return;
    const { locationMap: freshMap } = buildCashflowLocationMap(
      this.context.store.getState(),
      this.state.custodianNameCashflowMap,
      {
        extractedAccounts: this.props.accountData,
        accountLocationMap: this.accountLocationMap,
        newCustodianIdByAccountId: this.newCustodianIdByAccountId
      }
    );
    for (const section of this.state.gridData.sheets[0].sections) {
      const name = section.name;
      if (this.importedCustodianNames.includes(name) || this.userOverriddenSectionNames.has(name)) continue;
      if (Object.prototype.hasOwnProperty.call(freshMap, name)) {
        this.locationMap[name] = freshMap[name];
      } else {
        delete this.locationMap[name];
      }
    }

    const allItems = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut];
    setMatchedCashflowIds(
      allItems,
      this.locationMap,
      this.state.dataExtracted.custodianIdCashflowMap,
      (existing, item) => existing.date === item.date
    );
    for (const section of this.state.gridData.sheets[0].sections) {
      if (this.importedCustodianNames.includes(section.name)) continue;
      for (const row of section.rows) {
        this.setGridRowItemState(section, row);
      }
    }
    this.setState({ gridData: this.state.gridData });
  }

  handleExtractedDataResponse(dataExtracted) {
    const custodianNameCashflowMap = dataExtracted.custodianNameCashflowMap;
    this.accountLocationMap = this.props.accountLocationMap || {};
    this.newCustodianIdByAccountId = this.props.newCustodianIdByAccountId || {};
    const { locationMap } = buildCashflowLocationMap(this.context.store.getState(), custodianNameCashflowMap, {
      extractedAccounts: this.props.accountData,
      accountLocationMap: this.accountLocationMap,
      newCustodianIdByAccountId: this.newCustodianIdByAccountId
    });
    this.locationMap = locationMap;

    const allItems = [...dataExtracted.cashIn, ...dataExtracted.cashOut];
    for (const item of allItems) {
      if (item.ignore === true) {
        this.ignoredItemIds.push(item.id);
      }
    }
    setMatchedCashflowIds(
      allItems,
      this.locationMap,
      dataExtracted.custodianIdCashflowMap,
      (existing, item) => existing.date === item.date
    );

    this.defaultLocationMap = { ...this.locationMap };
    this.setState({
      dataExtracted: dataExtracted,
      custodianNameCashflowMap: custodianNameCashflowMap,
      gridData: this.getGridData(this.props.currentPortfolio.currency, custodianNameCashflowMap)
    });
  }

  getRowCountToImport() {
    if (!this.state.gridData === true) {
      return 0;
    }
    const mappedSectionNames = Object.keys(this.locationMap);
    return this.state.gridData.sheets[0].sections
      .filter(item =>
        this.state.disableImportButton
          ? this.importedCustodianNames.includes(item.name)
          : mappedSectionNames.includes(item.name) && !this.importedCustodianNames.includes(item.name)
      )
      .reduce((acc, section) => {
        const rows = section.rows.filter(
          item => item.cells[0].itemState !== itemState.IGNORED && !item.cells[1].invalidInputText === true
        );
        return acc + rows.length;
      }, 0);
  }

  handleGridChange(newGridData) {
    this.setState({ gridData: newGridData });
  }

  updateGridRow(gridData, newRow, sheetIndex, sectionIndex, rowIndex) {
    const newGridData = gridData;
    newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = newRow;
    this.setState({ gridData: newGridData });
  }

  handleCellInvalidTickerAdded(gridData, sheetIndex, sectionIndex, rowIndex, cellIndex) {
    const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
    const cell = row.cells[cellIndex];

    if (!cell.invalidInputText === false && cell.loading === false) {
      let invalidInputText = cell.invalidInputText;
      cell.loading = true;
      this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);

      const date = guessDateInCustodianHistoryUIFormat(row.cells[1].value).date;
      this.props.fetchTickerDetails(
        invalidInputText,
        date,
        result => {
          const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
          const cell = row.cells[cellIndex];

          cell.loading = false;
          if (!result === true) {
            this.props.showToastTip("TIP", i18n.t("invalidTickerError"), null, 10000);
            this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);
            return;
          }

          cell.exchangeRateDetails = result.exchangeRateDetails;
          cell.currency = result.tickerShortName;
          cell.invalidInputText = null;

          const newRow = row.clone();
          this.updateGridRow(gridData, newRow, sheetIndex, sectionIndex, rowIndex);
        },
        error => {
          const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
          const cell = row.cells[cellIndex];
          cell.loading = false;
          this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);
          this.props.showToastTip("TIP", i18n.t("tickerFetchFailure"), null, 10000);
        }
      );
    }
  }

  setGridRowItemState(section, row, cashflow = undefined) {
    if (!cashflow) {
      cashflow = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
        item => item.id === row.id
      );
    }

    if (this.ignoredItemIds.includes(row.id)) {
      row.cells[0].itemState = itemState.IGNORED;
    } else if (!this.locationMap[section.name] === true) {
      row.cells[0].itemState = itemState.UNKNOWN;
    } else {
      row.cells[0].itemState = cashflow.matchedCashflowId ? itemState.UPDATE : itemState.NEW;
    }
  }

  populateGridRow(section, row, cashflow) {
    row.id = cashflow.id;
    row.cells[1].value = convertCustodianHistoryApiDateFormatToUIFormat(cashflow.date);
    row.cells[2].value = cashflow.description;

    const cashflowDetails = getCashflowItemDetails(this.context.store.getState(), cashflow);
    if (cashflow.type === cashFlowType.CASH_IN) {
      row.cells[3].value = cashflowDetails.value;
      row.cells[3].currency = cashflowDetails.currency;
      row.cells[3].exchangeRateDetails = cashflowDetails.valueExchangeRate;
      row.cells[4].value = undefined;
      row.cells[4].currency = this.props.currentPortfolio.currency;
    } else if (cashflow.type === cashFlowType.CASH_OUT) {
      row.cells[3].value = undefined;
      row.cells[3].currency = this.props.currentPortfolio.currency;
      row.cells[4].value = cashflowDetails.value;
      row.cells[4].currency = cashflowDetails.currency;
      row.cells[4].exchangeRateDetails = cashflowDetails.valueExchangeRate;
    }
    this.setGridRowItemState(section, row, cashflow);
  }

  getGridData(currency, custodianNameCashflowMap) {
    var sections = [];
    for (const [index, [name, cashflows]] of Object.entries(Object.entries(custodianNameCashflowMap))) {
      const section = this.getEmptySection(name, `${index + 1}`);
      section.isCollapsed = index > 0;
      sections.push(section);

      var rows = [];
      const sortedCashflows = [...cashflows].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      for (const [index, cashflow] of sortedCashflows.entries()) {
        const row = this.getEmptyRow(`${index + 1}`, cashflow);
        this.populateGridRow(section, row, cashflow);
        rows.push(row);
      }
      section.rows = rows;
    }

    const sheet = this.getEmptySheet("1");
    sheet.sections = sections;

    const gridData = new GridData(currency, [sheet]);
    gridData.forceShowSheetsTitles = false;
    return gridData;
  }

  getEmptySheet(sortKey) {
    return new GridSheetData(getUuid(), sortKey, null, []);
  }

  getSectionState(section) {
    let count = section.rows.length;
    let displayString = `${count} ${i18n.t("rows")}`;
    if (this.importedCustodianNames.includes(section.name)) {
      count = section.rows.filter(
        item => item.cells[0].itemState !== itemState.IGNORED && this.ignoredItemIds.includes(item.id) === false
      ).length;
      displayString = (this.state.isImportingCustodianNames?.includes(section.name)
        ? i18n.t("importingRows")
        : i18n.t("importedRows")
      )?.replace("%s1%", count);
    } else if (this.ignoredCustodianNames.includes(section.name)) {
      displayString = i18n.t("ignoredRows").replace("%s1%", count);
    }
    return count === 1 ? displayString.slice(0, -1) : displayString;
  }

  getEmptySection(name, sortKey) {
    const badgeColumn = new GridColumnData("", false, false, true);
    badgeColumn.width = itemStateBadgeCellWidth;
    const dateColumn = new GridColumnData(i18n.t("date"), true, true, false);
    const notesColumn = new GridColumnData(i18n.t("notes"), true, true, false);
    const cashInColumn = new GridColumnData(i18n.t("cashIn"), true, true, false);
    cashInColumn.headerCellCss = css`
      margin-right: 50px;
    `;
    const cashOutColumn = new GridColumnData(i18n.t("cashOut"), true, true, false);
    const optionsColumn = new GridColumnData(null, false, false, true);
    const columns = [badgeColumn, dateColumn, notesColumn, cashInColumn, cashOutColumn, optionsColumn];

    const sectionData = new GridSectionData(getUuid(), sortKey, name, [], columns, undefined, 4, false);
    sectionData.getContextMenuItems = section => {
      return [
        [this.ignoredCustodianNames.includes(section.name) ? contextMenuItemType.UNIGNORE : contextMenuItemType.IGNORE]
      ];
    };
    sectionData.forceShowTitle = true;
    sectionData.showFooter = false;
    sectionData.isDragDisabled = true;

    sectionData.onRenderTitle = (sheetIndex, sectionIndex, section) => {
      const location = this.locationMap[section.name];
      const isSectionImported = this.importedCustodianNames.includes(section.name);

      return (
        <SectionTitle isFirst={sectionIndex === 0}>
          {sectionIndex !== 0 && <SectionPadding></SectionPadding>}
          <SectionTitleContainer isFirst={sectionIndex === 0}>
            <ExpandIcon isCollapsed={section.isCollapsed} />
            <SectionName>{section.name}</SectionName>
            <Arrow>{"→"}</Arrow>
            <SectionDetailsContainer
              onClick={e => {
                if (isSectionImported === false) {
                  this.handleSectionNameClick(e, section);
                }
              }}
            >
              <CustodianName isCustodianPath={!location === false && location.isCustodian === true}>
                {!location === true ? i18n.t("aiImport.pickCashflowCustodian") : location.name}
              </CustodianName>
              <SectionDescription>
                {getLocationPath(
                  this.context.store.getState(),
                  this.props.currentPortfolio.id,
                  location,
                  i18n.t("aiImport.pickCashflowCustodianDesc")
                )}
              </SectionDescription>
            </SectionDetailsContainer>
            <SectionState isImported={isSectionImported}>{this.getSectionState(section)}</SectionState>
            {isSectionImported === false && (
              <SectionOptionsButton onClick={e => this.handleSectionOptionButtonClick(e, section)}>
                <OptionsIconComponent />
              </SectionOptionsButton>
            )}
          </SectionTitleContainer>
        </SectionTitle>
      );
    };
    sectionData.modifyHeaderCss = css`
      height: 27px;
    `;
    return sectionData;
  }

  getEmptyRow(sortKey, cashflow) {
    const badgeCell = new GridCellData(cellType.CLICKABLE_TEXT, "", null);
    badgeCell.width = itemStateBadgeCellWidth;
    badgeCell.textAlignment = "right";
    badgeCell.getAccessoryView = (sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      const section = this.state.gridData.sheets[sheetIndex].sections[sectionIndex];
      const isSectionImported = this.importedCustodianNames.includes(section.name);
      const cell = section.rows[rowIndex].cells[cellIndex];
      return (
        <BadgeContainer>
          <BadgeText
            bgColor={getBadgeColorForItemState(cell.itemState, isSectionImported)}
            itemState={cell.itemState}
            isDisabled={isSectionImported}
          >
            {cell.itemState}
          </BadgeText>
        </BadgeContainer>
      );
    };
    const dateCell = new GridCellData(cellType.TEXT, i18n.t("date"), null);
    dateCell.textAlignment = "left";

    const notesCell = new GridCellData(cellType.TEXT, i18n.t("notes"), null);
    notesCell.textAlignment = "left";
    notesCell.emptyValueIndicator = "---";

    const cashInCell = new CurrencyCellData(
      cellType.CURRENCY,
      i18n.t("cashIn"),
      null,
      this.props.currentPortfolio.currency
    );
    cashInCell.emptyValueIndicator = "---";
    cashInCell.useRateFromExchangeRateDetails = true;
    cashInCell.supportedTickerTypes = [tickerTypes.FIAT, tickerTypes.CRYPTO];
    cashInCell.unsupportedTickerSubTypes = [tickerSubTypes.PRECIOUS_METALS];
    cashInCell.getAccessoryView = (sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      return (
        <SwapCashflowIcon
          onClick={() => {
            const newGridData = this.state.gridData;
            const row = newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
            const section = newGridData.sheets[sheetIndex].sections[sectionIndex];
            newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
            const cashflow = {
              ...[...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
                item => item.id === row.id
              )
            };
            cashflow.type = row.cells[3].value === undefined ? cashFlowType.CASH_IN : cashFlowType.CASH_OUT;
            this.populateGridRow(section, row, cashflow);
            this.setState({ gridData: newGridData });
          }}
        />
      );
    };
    const cashOutCell = new CurrencyCellData(
      cellType.CURRENCY,
      i18n.t("cashOut"),
      null,
      this.props.currentPortfolio.currency
    );
    cashOutCell.emptyValueIndicator = "---";
    cashOutCell.useRateFromExchangeRateDetails = true;
    cashOutCell.supportedTickerTypes = [tickerTypes.FIAT, tickerTypes.CRYPTO];
    cashOutCell.unsupportedTickerSubTypes = [tickerSubTypes.PRECIOUS_METALS];

    const optionCell = new GridCellData(cellType.OPTIONS, "", null);
    const cells = [badgeCell, dateCell, notesCell, cashInCell, cashOutCell, optionCell];

    const rowData = new GridRowData(getUuid(), sortKey, "entry-id-" + Math.random(), cells, 2, false, () => {
      return true;
    });
    rowData.showHint = false;
    rowData.getContextMenuItems = (row, rowIndex, sectionIndex, sheetIndex) => {
      const section = this.state.gridData.sheets[sheetIndex].sections[sectionIndex];
      return [
        this.locationMap[section.name]
          ? [contextMenuItemType.MAP_TO_ANOTHER, contextMenuItemType.RESET_CHANGES]
          : [contextMenuItemType.RESET_CHANGES],
        [row.cells[0].itemState === itemState.IGNORED ? contextMenuItemType.UNIGNORE : contextMenuItemType.IGNORE]
      ];
    };
    return rowData;
  }

  handlePendingItemsChange(closeDialogIfAllTabsDone = false) {
    this.props.onPendingItemsChange(
      this.props.tabKey,
      this.importedItemIds.length + this.ignoredItemIds.length,
      closeDialogIfAllTabsDone
    );
  }

  handleRowContextMenuSelection(sheetIndex, sectionIndex, rowIndex, row, menuItem) {
    const section = this.state.gridData.sheets[sheetIndex].sections[sectionIndex];

    if (menuItem.id === contextMenuItemType.MAP_TO_ANOTHER.id) {
      this.setState({ showLocationPickerForRow: row, showLocationPickerForRowSection: section });
    } else if (menuItem.id === contextMenuItemType.RESET_CHANGES.id) {
      const newGridData = this.state.gridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      const cashflow = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
        item => item.id === row.id
      );
      this.populateGridRow(section, row, cashflow);
      this.setState({ gridData: newGridData });
    } else if (menuItem.id === contextMenuItemType.IGNORE.id) {
      const newGridData = this.state.gridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      row.cells[0].itemState = itemState.IGNORED;
      this.ignoredItemIds.push(row.id);
      this.setState({ gridData: newGridData });
      this.handlePendingItemsChange();
    } else if (menuItem.id === contextMenuItemType.UNIGNORE.id) {
      const newGridData = this.state.gridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      this.ignoredItemIds = this.ignoredItemIds.filter(item => item !== row.id);
      this.setGridRowItemState(section, row);
      this.setState({ gridData: newGridData });
      this.handlePendingItemsChange();
    }
  }

  handleSectionNameClick(e, section) {
    e.stopPropagation();
    this.setState({ showLocationPickerForSection: section });
  }

  handleSectionOptionButtonClick(e, section) {
    e.stopPropagation();

    if (this.contextMenuRef.current.isVisible() === true) {
      this.contextMenuRef.current.dismiss();
      return;
    }
    const targetPosition = e.target.getBoundingClientRect();
    const menuItems = section.getContextMenuItems(section);

    this.contextMenuRef.current.show(
      menuItems,
      targetPosition.left + targetPosition.width,
      targetPosition.top + targetPosition.height + 2,
      false,
      e.target
    );
    this.setState({ showContextMenuForSection: section });
  }

  handleSectionContextMenuSelection(item) {
    const section = this.state.showContextMenuForSection;

    if (item.id === contextMenuItemType.IGNORE.id) {
      const ignoredItemIds = section.rows.map(row => row.id);
      this.ignoredItemIds.push(...ignoredItemIds);
      this.ignoredCustodianNames.push(section.name);
      for (const row of section.rows) {
        this.setGridRowItemState(section, row);
      }
      this.setState({ showContextMenuForSection: null, gridData: this.state.gridData });
      this.handlePendingItemsChange();
    } else if (item.id === contextMenuItemType.UNIGNORE.id) {
      const unignoredItemIds = section.rows.map(row => row.id);
      this.ignoredItemIds = this.ignoredItemIds.filter(item => !unignoredItemIds.includes(item));
      this.ignoredCustodianNames = this.ignoredCustodianNames.filter(item => item !== section.name);
      for (const row of section.rows) {
        this.setGridRowItemState(section, row);
      }
      this.setState({ showContextMenuForSection: null, gridData: this.state.gridData });
      this.handlePendingItemsChange();
    }
  }

  handleRowLocationPickerDismiss() {
    this.setState({ showLocationPickerForRow: null, showLocationPickerForRowSection: null });
  }

  handleRowLocationSelection(section, row, value) {
    const updateSection = (targetSection, isNew = false) => {
      const newGridData = this.state.gridData;
      if (targetSection.name === section.name) {
        return;
      }

      if (isNew) {
        newGridData.sheets[0].sections.push(targetSection);
        newGridData.sheets[0].sections.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      }

      const cashflow = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
        item => item.id === row.id
      );
      cashflow.name = targetSection.name;

      section.rows = section.rows.filter(item => item.id !== row.id);
      targetSection.rows.push(row);
      setMatchedCashflowIds(
        [cashflow],
        this.locationMap,
        this.state.dataExtracted.custodianIdCashflowMap,
        (existing, item) => existing.date === item.date
      );
      this.setGridRowItemState(targetSection, row);
      targetSection.rows.sort((a, b) =>
        (converUIDateFormatToCustodianHistoryApiDateFormat(b.cells[1].value) || "").localeCompare(
          converUIDateFormatToCustodianHistoryApiDateFormat(a.cells[1].value) || ""
        )
      );
      this.setState({ gridData: newGridData });
    };

    const matchingLocationEntry = Object.entries(this.locationMap).find(([name, location]) => {
      return location.id === value.id;
    });
    if (matchingLocationEntry) {
      const matchingEntrySectionName = matchingLocationEntry[0];
      const targetSection = this.state.gridData.sheets[0].sections.find(item => item.name === matchingEntrySectionName);
      updateSection(targetSection);
    } else {
      const insertNewSection = () => {
        const sectionName = value.name;
        this.userOverriddenSectionNames.add(sectionName);
        this.locationMap[sectionName] = value;

        const sections = this.state.gridData.sheets[0].sections;
        const currentSectionIndex = this.state.gridData.sheets[0].sections.findIndex(
          item => item.name === section.name
        );
        const sortKey = getSortKeyBetween(
          section.sortKey,
          currentSectionIndex === sections.length - 1 ? undefined : sections[currentSectionIndex + 1].sortKey
        );
        const targetSection = this.getEmptySection(sectionName, sortKey);
        updateSection(targetSection, true);
      };

      const custodianIdCashflowMap = this.state.dataExtracted.custodianIdCashflowMap;
      if (custodianIdCashflowMap[value.id]) {
        insertNewSection();
      } else {
        this.setState({ isLoadingCashflow: true });
        this.props
          .getCustodianCashflow(value.id)
          .then(cashflows => {
            const newDataExtracted = this.state.dataExtracted;
            newDataExtracted.custodianIdCashflowMap[value.id] = cashflows;
            this.setState({ dataExtracted: newDataExtracted, isLoadingCashflow: false });
            insertNewSection();
          })
          .catch(() => {});
      }
    }
  }

  handleSectionLocationPickerDismiss() {
    this.setState({ showLocationPickerForSection: null, showLocationPickerForDebtRow: null });
  }

  handleSectionLocationSelection(section, value) {
    const updateSection = () => {
      this.userOverriddenSectionNames.add(section.name);
      this.locationMap[section.name] = value;

      for (const row of section.rows) {
        const cashflow = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
          item => item.id === row.id
        );
        setMatchedCashflowIds(
          [cashflow],
          this.locationMap,
          this.state.dataExtracted.custodianIdCashflowMap,
          (existing, item) => existing.date === item.date
        );
        this.setGridRowItemState(section, row);
      }
      this.setState({ gridData: this.state.gridData, disableImportButton: false });
    };

    const custodianIdCashflowMap = this.state.dataExtracted.custodianIdCashflowMap;
    if (custodianIdCashflowMap[value.id]) {
      updateSection();
    } else {
      this.setState({ isLoadingCashflow: true });
      this.props
        .getCustodianCashflow(value.id)
        .then(cashflows => {
          const newDataExtracted = this.state.dataExtracted;
          newDataExtracted.custodianIdCashflowMap[value.id] = cashflows;
          this.setState({ dataExtracted: newDataExtracted, isLoadingCashflow: false });
          updateSection();
        })
        .catch(() => {});
    }
  }

  handleCellBlur(sheetIndex, sectionIndex, rowIndex, cellIndex) {
    if (cellIndex === 1) {
      const cell = this.state.gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex].cells[cellIndex];
      const cellValue = cell.value;
      if (!cellValue === true) {
        return;
      }

      const guessedDate = guessDateInCustodianHistoryUIFormat(cellValue);
      const newGridData = this.state.gridData;
      let dateString = guessedDate.dateString;
      if (guessedDate.isInvalid === true) {
        this.props.showToastTip("TIP", i18n.t("invalidDateEntry"), null, 3000);
        if (!dateString === true) {
          dateString = cellValue;
        }
        cell.invalidInputText = cellValue;
      } else {
        cell.invalidInputText = null;
      }
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex].cells[1].value = dateString;
      this.setState({ gridData: newGridData });
    }
  }

  getSectionLocationId(section) {
    const location = this.locationMap[section.name];
    return location.newCustodianId || location.id;
  }

  handleImportButtonClick(e) {
    this.runSync();
  }

  // Sync this tab's cash flows in their own bulk submit. When rows map to in-session accounts
  // (isNewAccount), defer to the parent's combined sync via the confirmation dialog so the accounts
  // are created first — unless bypassConfirmation is set (the combined sync invoking us directly,
  // after accounts already exist). options.onSynced fires after the flush completes.
  getSectionsToImport() {
    const mappedSectionNames = Object.keys(this.locationMap);
    return this.state.gridData.sheets[0].sections.filter(
      item => mappedSectionNames.includes(item.name) && !this.importedCustodianNames.includes(item.name)
    );
  }

  runSync(options = {}) {
    const sectionsToImport = this.getSectionsToImport();

    // isNewAccount already reflects synced accounts (the map is refreshed on sync), so no extra check.
    const hasNewAccountSections = sectionsToImport.some(item => this.locationMap[item.name]?.isNewAccount === true);
    if (hasNewAccountSections && !options.bypassConfirmation) {
      this.setState({ showCombinedSyncConfirmation: true });
      return;
    }

    let ignoredHashes = [];
    let usedHashes = [];
    let addedItemsCount = 0;
    let updatedItemsCount = 0;
    let ignoredItemsCount = 0;

    let custodianIdSectionsMap = {};
    for (const section of sectionsToImport) {
      const locationId = this.getSectionLocationId(section);
      if (custodianIdSectionsMap[locationId]) {
        custodianIdSectionsMap[locationId].push(section);
      } else {
        custodianIdSectionsMap[locationId] = [section];
      }
    }

    const importedCustodianNamesCountBeforeSync = this.importedCustodianNames.length;
    const importedItemIdsCountBeforeSync = this.importedItemIds.length;

    const custodianCashflowMap = {};
    for (const sections of Object.values(custodianIdSectionsMap)) {
      this.importedCustodianNames.push(...sections.map(item => item.name));
      const rows = sections.reduce((acc, section) => {
        return acc.concat(section.rows);
      }, []);
      if (rows.length > 0) {
        const dateCashflowMap = {};
        for (const row of rows) {
          const extractedData = [...this.state.dataExtracted.cashIn, ...this.state.dataExtracted.cashOut].find(
            item => item.id === row.id
          );
          if (extractedData.ignore === true && row.cells[0].itemState !== itemState.IGNORED) {
            usedHashes.push(extractedData.hash);
          } else if (!extractedData.ignore === true && row.cells[0].itemState === itemState.IGNORED) {
            ignoredHashes.push(extractedData.hash);
          }

          if (row.cells[0].itemState === itemState.IGNORED) {
            ignoredItemsCount++;
            continue;
          }
          this.importedItemIds.push(row.id);
          if (
            !row.cells[1].invalidInputText === false ||
            (!row.cells[3].value === true && !row.cells[4].value === true)
          ) {
            continue;
          }
          const date = row.cells[1].value;
          var cashflow = dateCashflowMap[date];
          if (!cashflow === true) {
            cashflow = {
              id: extractedData.matchedCashflowId || getUuid(),
              isUpdate: !extractedData.matchedCashflowId === false,
              date: row.cells[1].value,
              lastLink: buildCashflowLastLink(extractedData.name)
            };
            dateCashflowMap[date] = cashflow;
          }

          if (cashflow.isUpdate) {
            updatedItemsCount++;
          } else {
            addedItemsCount++;
          }

          if (!row.cells[2].value === false) {
            cashflow.note = cashflow.note === undefined ? row.cells[2].value : cashflow.note + ` ${row.cells[2].value}`;
          }
          if (!row.cells[3].value === false) {
            cashflow.cashIn = cashflow.cashIn === undefined ? row.cells[3].value : cashflow.cashIn + row.cells[3].value;
            cashflow.cashInTickerId = row.cells[3].getTickerId(this.context.store.getState());
            cashflow.cashInExchangeRate = row.cells[3].exchangeRateDetails;
          }
          if (!row.cells[4].value === false) {
            cashflow.cashOut =
              cashflow.cashOut === undefined ? row.cells[4].value : cashflow.cashOut + row.cells[4].value;
            cashflow.cashOutTickerId = row.cells[4].getTickerId(this.context.store.getState());
            cashflow.cashOutExchangeRate = row.cells[4].exchangeRateDetails;
          }
          dateCashflowMap[date] = cashflow;
        }
        if (Object.values(dateCashflowMap).length > 0) {
          custodianCashflowMap[this.getSectionLocationId(sections[0])] = Object.values(dateCashflowMap);
        }
      }
    }

    this.setState({ isImportingCustodianNames: sectionsToImport.map(item => item.name) });
    this.props.onImportingStatusChange(true);

    const bulkCollector = createBulkSyncCollector();
    syncCashflowsToPortfolio({
      dispatch: this.context.store.dispatch,
      state: this.context.store.getState(),
      portfolio: this.props.currentPortfolio,
      documentId: this.props.documentId,
      custodianCashflowMap,
      entityType: bulkEntityType.CASH_FLOW,
      ignoredHashes,
      usedHashes,
      addedCount: addedItemsCount,
      updatedCount: updatedItemsCount,
      ignoredCount: ignoredItemsCount,
      lastLinkMetadata: this.props.lastLinkMetadata,
      bulkCollector
    });

    flushBulkSync({
      dispatch: this.context.store.dispatch,
      portfolio: this.props.currentPortfolio,
      documentId: this.props.documentId,
      bulkCollector,
      onComplete: () => {
        this.handlePendingItemsChange(true);
        const newGridData = this.state.gridData;
        for (const section of newGridData.sheets[0].sections) {
          section.isEditable = this.importedCustodianNames.includes(section.name) === false;
          section.rows.forEach(row => (row.cells[5].hide = true));
        }
        this.setState({ disableImportButton: true, gridData: newGridData, isImportingCustodianNames: null });
        this.props.onImportingStatusChange(false);
        options.onSynced?.();
      },
      onError: () => {
        this.importedCustodianNames.length = importedCustodianNamesCountBeforeSync;
        this.importedItemIds.length = importedItemIdsCountBeforeSync;
        this.setState({ isImportingCustodianNames: null });
        this.props.onImportingStatusChange(false);
        options.onError?.();
      }
    });
  }

  render() {
    if (this.state.dataExtracted) {
      const gridOptions = {
        getRowStyle: (row, rowIndex, sectionIndex) => {
          return {
            height: "45px",
            backgroundColor: row.cells[0].itemState === itemState.IGNORED ? "#EBEBEB" : "white"
          };
        }
      };

      const rowCountToImport = this.getRowCountToImport();
      const importedKey = this.props.isReviewDataFlow ? "aiImport.syncedRows" : "aiImport.importedRows";
      const sliceIfSingular = string => (rowCountToImport !== 1 ? string : string.slice(0, -1));
      let importButtonString;
      if (this.state.disableImportButton) {
        importButtonString = sliceIfSingular(i18n.t(importedKey).replace("%s1%", this.importedItemIds.length));
      } else if (this.props.isReviewDataFlow) {
        importButtonString = i18n.t("aiSync.confirmAndSync");
      } else {
        importButtonString = sliceIfSingular(i18n.t("aiImport.importRows").replace("%s1%", rowCountToImport));
      }
      const totalResults = this.state.dataExtracted.cashIn.length + this.state.dataExtracted.cashOut.length;

      return (
        <Container>
          <ContentContainer>
            <ActionButtonsContainer>
              <ImportButton
                loaderCss={css`
                  border-color: rgb(229, 229, 229, 0.5);
                  border-top-color: black;
                `}
                isDisabled={
                  this.state.disableImportButton ||
                  rowCountToImport === 0 ||
                  !this.state.isImportingCustodianNames === false
                }
                onClick={this.handleImportButtonClick}
                title={importButtonString}
                isLoading={!this.state.isImportingCustodianNames === false}
              />
              <RescanHelpText>{i18n.t("aiImport.rescanHelp")}</RescanHelpText>
              <RescanButton onClick={this.props.onRescan} title={i18n.t("rescan")} />
            </ActionButtonsContainer>
            <ResultsContainer>
              {this.props.isDataPartial === true && (
                <PartialResultMessage>
                  {i18n.t("aiImport.partialResultMessage").replace("%s1%", Math.floor((totalResults - 8) / 5) * 5)}
                </PartialResultMessage>
              )}
              {this.state.gridData && this.state.gridData.sheets[0].sections.length > 0 && (
                <GridContainer>
                  <Grid
                    gridOptions={gridOptions}
                    gridData={this.state.gridData}
                    getEmptyRow={this.getEmptyRow}
                    onChange={this.handleGridChange}
                    onPaste={this.handleCellBlur}
                    onCellBlur={this.handleCellBlur}
                    onSectionUpdate={() => {}}
                    onCellInvalidTickerAdded={(sheetIndex, sectionIndex, rowIndex, cellIndex) =>
                      this.handleCellInvalidTickerAdded(
                        this.state.gridData,
                        sheetIndex,
                        sectionIndex,
                        rowIndex,
                        cellIndex
                      )
                    }
                    onRowContextMenuSelection={this.handleRowContextMenuSelection}
                  />
                </GridContainer>
              )}
            </ResultsContainer>
          </ContentContainer>
          <ContextMenu
            ref={this.contextMenuRef}
            width={233}
            onSelection={this.handleSectionContextMenuSelection}
            onDismiss={() => this.setState({ showContextMenuForSection: null })}
          />
          {!this.state.showLocationPickerForSection === false && (
            <CustodiansPickerDialog
              isLocation={true}
              data={{ props: { onlyCustodians: true } }}
              category={category.ASSET}
              subTitle={this.state.showLocationPickerForSection.name}
              allowCategoryChange={false}
              assetDescription={i18n.t("aiImport.assetPickerDesc")}
              filterResults={result => {
                return result.isCustodian === true;
              }}
              returnResultWithDetails={true}
              onVariableUpdate={data =>
                this.handleSectionLocationSelection(this.state.showLocationPickerForSection, data.items[0])
              }
              onDismiss={this.handleSectionLocationPickerDismiss}
            />
          )}
          {!this.state.showLocationPickerForRow === false && (
            <CustodiansPickerDialog
              isLocation={true}
              data={{ props: { onlyCustodians: true } }}
              category={category.ASSET}
              subTitle={this.state.showLocationPickerForRow.cells[1].value}
              allowCategoryChange={false}
              assetDescription={i18n.t("aiImport.assetPickerDesc")}
              filterResults={result => {
                return result.isCustodian === true;
              }}
              returnResultWithDetails={true}
              onVariableUpdate={data =>
                this.handleRowLocationSelection(
                  this.state.showLocationPickerForRowSection,
                  this.state.showLocationPickerForRow,
                  data.items[0]
                )
              }
              onDismiss={this.handleRowLocationPickerDismiss}
            />
          )}
          {this.state.isLoadingCashflow === true && <CashflowsLoader />}
          {this.state.showCombinedSyncConfirmation && (
            <ConfirmationDialog
              canUserDismiss={false}
              title={i18n.t("aiSync.combinedSyncTitle")}
              description={i18n.t("aiSync.combinedSyncDesc")}
              positiveButtonTitle={i18n.t("aiSync.combinedSyncConfirm")}
              negativeButtonTitle={i18n.t("cancel")}
              handlePositiveButtonClick={() => {
                this.setState({
                  showCombinedSyncConfirmation: false,
                  isImportingCustodianNames: this.getSectionsToImport().map(item => item.name)
                });
                this.props.onRunCombinedSync?.({ onAbort: () => this.setState({ isImportingCustodianNames: null }) });
              }}
              handleNegativeButtonClick={() => this.setState({ showCombinedSyncConfirmation: false })}
            />
          )}
        </Container>
      );
    }
  }
}

const mapStateToProps = (state, props) => ({
  currentPortfolio: currentPortfolioSelector(state),
  userPreferences: userPreferencesSelector(state),
  aiSupportedFileTypes: aiSupportedFileTypesSelector(state)
});

const mapDispatchToProps = {
  uploadAiImportDocument,
  insertCustodianAtEndOfSection,
  insertSection,
  updateUserPreferences,
  rescanAiImportDocument,
  showBlackPaywallIfQualifies,
  showToastTip: showToastTip,
  fetchTickerDetails: fetchTickerDetails,
  getCustodianCashflow: getCustodianCashflow
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AiCashflowImportComponent));
